import type {
  Asset,
  AudioAsset,
  ImageAsset,
  VideoAsset,
} from "@edclub/shared-types";

import type { WindowWithAppRouter } from "../ts-utils";

export const LIBRARY_IMAGE_REGEX = /\bed(?:image|media):\d+\b/;
export const LIBRARY_AUDIO_REGEX = /\bed(?:audio):\d+\b/;
export const LIBRARY_VIDEO_REGEX = /\bed(?:video):\d+\b/;

export type GetAssetByTagOptions = {
  fallbackAsset?: string;
};

export type FallbackAsset = {
  url: string;
  fileName: string;
  width: 0;
  height: 0;
};

export type ImageTag = `edimage:${number}` | `edmedia:${number}`;
export type AudioTag = `edaudio:${number}`;
export type VideoTag = `edvideo:${number}`;
export type AllowedTags = ImageTag | AudioTag | VideoTag;

type CommonAssetFields<T extends object> = {
  fileName: string;
} & T;

export type ImageAssetWithFileName = CommonAssetFields<ImageAsset>;
export type AudioAssetWithFileName = CommonAssetFields<AudioAsset>;
export type VideoAssetWithFileName = CommonAssetFields<VideoAsset>;

export type AssetType =
  | ImageAssetWithFileName
  | AudioAssetWithFileName
  | VideoAssetWithFileName
  | FallbackAsset;

export function isImageTag(tag: string): tag is ImageTag {
  return LIBRARY_IMAGE_REGEX.test(tag);
}

export function isAudioTag(tag: string): tag is AudioTag {
  return LIBRARY_AUDIO_REGEX.test(tag);
}

export function isVideoTag(tag: string): tag is VideoTag {
  return LIBRARY_VIDEO_REGEX.test(tag);
}

/**
 * tag: edimage:<number>, edmedia:<number>, edaudio:<number>
 */
export function getLibraryAsset(tag: AudioTag): AudioAsset | null;
export function getLibraryAsset(tag: ImageTag): ImageAsset | null;
export function getLibraryAsset(tag: VideoTag): VideoAsset | null;
export function getLibraryAsset(tag: string): Asset | null;

export function getLibraryAsset(tag: string): Asset | null {
  const asset = (window as unknown as WindowWithAppRouter).approuter.assets[
    tag
  ];

  if (!asset) {
    console.error(`Missing asset: ${tag}`);

    return null;
  }

  return asset;
}
